import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { TenantAnalyticsFilter } from './TenantAnalyticsFilter'
import { useGetRequest, useLoad } from '../../../shared/hooks/requests'
import { TENANT_ANALYTICS_FILTER, TENANT_ANALYTICS_MTTS, TENANT_ANALYTICS_PACKAGES } from '../../../shared/utils/urls'
import { useFilter } from '../../../app/contexts/FilterContextProvider'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import { AttendanceBlank } from './AttendanceModal'
import { EmptyFolderIcon } from '../../../components/svgs/Svgs'
import { DeviceAnalytics } from './DeviceAnalytics'
import { AnalyticsLogs } from './AnalyticsLogs'
import Table from '../../../shared/ui/Table'
import { PackagesTableItems } from './PackagesTableItems'
import { AddBlackListModal } from './AddBlackListModal'
import { DuplicatedImages } from './DuplicatedImages'

const cols = [
    { id: 1, title: 'AppLicensingVerdict' },
    { id: 2, title: 'AppRecognitionVerdict' },
    { id: 3, title: 'AppVersionCode' },
    { id: 4, title: 'AppVersionName' },
    { id: 5, title: 'AttendanceCount' },
    { id: 6, title: 'CreatedAt' },
    { id: 7, title: 'DeviceId' },
    { id: 8, title: 'DeviceModel' },
    { id: 9, title: 'DeviceName' },
    { id: 10, title: 'IpAddress' },
]

export function TenantAnalyticsTable() {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const lastAnalyticsFilter = useRef(null)
    const location = useLocation()
    const id = location.state?.id
    const [showAttendanceBlank, setShowAttendanceBlank] = useState('')
    const [selectedAttendanceData, setSelectedAttendanceData] = useState(null)
    const { dateFilter } = useFilter()
    const { analyticsFilter } = useFilter()
    const getAnalytics = useGetRequest()
    const getEntities = useLoad({ url: TENANT_ANALYTICS_MTTS }, [])
    const getPackages = useGetRequest()
    const analytics = getAnalytics.response ? getAnalytics.response : null
    const entities = getEntities.response ? getEntities.response : null
    const attendances = analytics?.attendances ? analytics.attendances : []
    const devices = analytics?.devices ? analytics?.devices : []
    const tenantEntity = analytics?.tenantEntity ? analytics?.tenantEntity : []
    const packages = getPackages.response ? getPackages.response.items : []
    const total = getPackages.response ? getPackages.response.total : 0

    const isNonEmptyObject = (obj) => obj && typeof obj === 'object' && Object.keys(obj).length > 0

    const getDateRangeFromStartPeriod = () => {
        if (!dateFilter?.startPeriod) return { startDate: null, endDate: null }

        const start = new Date(dateFilter.startPeriod)
        const year = start.getFullYear()
        const month = start.getMonth()

        // Construct date strings manually to avoid timezone offset issues
        const formatDate = (y, m, d) => {
            const mm = String(m + 1).padStart(2, '0')
            const dd = String(d).padStart(2, '0')
            return `${y}-${mm}-${dd}`
        }

        const startDate = formatDate(year, month, 1) // first day of current month
        const endDate = formatDate(month === 11 ? year + 1 : year, (month + 1) % 12, 1) // first day of next month

        return { startDate, endDate }
    }

    const { startDate, endDate } = getDateRangeFromStartPeriod()

    useEffect(() => {
        // prevent if search and district are both empty
        if (!(analyticsFilter.district || analyticsFilter.search)) return

        // stringify for shallow comparison
        const currentFilterKey = JSON.stringify({
            start_date: dateFilter.startPeriod,
            end_date: dateFilter.endPeriod,
            search: analyticsFilter.search,
            region: analyticsFilter.region,
            district: analyticsFilter.district,
        })

        // check if filters have actually changed
        if (lastAnalyticsFilter.current !== currentFilterKey) {
            getAnalytics.request({
                url: TENANT_ANALYTICS_FILTER,
                params: {
                    start_date: dateFilter.startPeriod,
                    end_date: dateFilter.endPeriod,
                    ...(analyticsFilter.search !== '' ? { search: analyticsFilter.search } : {}),
                    ...(analyticsFilter.region !== '' ? { region_id: analyticsFilter.region } : {}),
                    ...(analyticsFilter.district !== '' ? { district_id: analyticsFilter.district } : {}),
                },
            })
            getEntities.request({
                url: TENANT_ANALYTICS_MTTS,
                params: {
                    start_date: startDate,
                    end_date: endDate,
                },
            })

            // update last filter
            lastAnalyticsFilter.current = currentFilterKey
        }
    }, [analyticsFilter, dateFilter, page, pageSize])

    useEffect(() => {
        if (analyticsFilter.search !== '') {
            getPackages.request({
                url: TENANT_ANALYTICS_PACKAGES,
                params: {
                    page,
                    size: pageSize,
                    ...(analyticsFilter.search !== '' ? { mtt_id: analyticsFilter.search } : {}),
                },
            })
        }
    }, [analyticsFilter, page, pageSize])

    const handleDetailsClick = ({ category, label, detailsClick, model }) => {
        setSelectedAttendanceData({
            start_date: dateFilter.startPeriod,
            end_date: dateFilter.endPeriod,
            id: tenantEntity?.id,
            region: analyticsFilter.region,
            district: analyticsFilter.district,
            category,
            label,
            model,
        })
        setShowAttendanceBlank(detailsClick)
    }

    return (

        <div className="w-full flex flex-col gap-6">

            {showAttendanceBlank === 'attendancesBlank' && selectedAttendanceData ? (
                <AttendanceBlank
                    startDate={selectedAttendanceData.start_date}
                    endDate={selectedAttendanceData.end_date}
                    id={selectedAttendanceData.id}
                    category={selectedAttendanceData.category}
                    label={selectedAttendanceData.label}
                    onBack={() => setShowAttendanceBlank(false)}
                    region={selectedAttendanceData.region}
                    district={selectedAttendanceData.district}
                />
            ) : showAttendanceBlank === 'devicesAnalytics' ? (
                <DeviceAnalytics
                    startPeriod={selectedAttendanceData.start_date}
                    endPeriod={selectedAttendanceData.end_date}
                    id={selectedAttendanceData.id}
                    category={selectedAttendanceData.category}
                    deviceId={selectedAttendanceData.label}
                    model={selectedAttendanceData.model}
                    onBack={() => setShowAttendanceBlank(false)}
                />
            ) : (
                <>
                    <TenantAnalyticsFilter redirectId={id} />
                    <div className="w-full h-full flex flex-col items-center gap-[20px]">
                        {/* entity info element */}
                        {isNonEmptyObject(analytics?.tenantEntity) && (
                            <div className="w-full flex flex-col items-start gap-[20px]
                            bg-white rounded-[6px] p-[15px]"
                            >
                                <div className="w-full flex items-center justify-between">
                                    <span className="text-black font-[600] whitespace-nowrap text-[14px]">
                                        Entity Info
                                    </span>
                                    <div className="flex items-center gap-[20px]">
                                        <AnalyticsLogs id={tenantEntity?.id} title="System logs" />
                                        {tenantEntity?.isBlacklisted ? (
                                            <div className="w-[30px] h-[30px] rounded-[50%] bg-red-800" />
                                        ) : (
                                            <AddBlackListModal id={tenantEntity?.id} />
                                        ) }
                                    </div>

                                </div>
                                <div className="w-full grid grid-cols-5 gap-[10px]">
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">Full Name:</span>
                                        <span className="text-[12px] text-center font-[500] text-gray-200">
                                            {tenantEntity?.name}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">Allowed Radius:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.allowedRadius}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">District Name:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.districtName}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">Region Name:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.regionName}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">
                                            Face Auth Threshold:
                                        </span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.faceAuthThreshold}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">
                                            Spoofing Threshold:
                                        </span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.spoofingThreshold}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">SyncKidsAt:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.syncKidsAt}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">LAT:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.lat}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">LON:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.lon}
                                        </span>
                                    </div>
                                    <div className="w-full flex flex-col items-center justify-center gap-[6px]">
                                        <span className="text-[14px] font-[600] text-black/70">MTTID:</span>
                                        <span className="text-[12px] font-[500] text-gray-200">
                                            {tenantEntity?.mttId}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* other parts */}
                        <div className="w-[100%] flex flex-col items-start gap-[20px] p-[15px] rounded-[6px] bg-white">
                            <span className="text-black text-[14px] font-[600]">Attendance info</span>
                            {getAnalytics.loading ? (
                                <div className="w-full h-[150px] flex flex-col ites-center justify-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                analytics ? (
                                    <div className="w-full flex flex-col items-center gap-[10px]">
                                        {[
                                            { label: 'Fake Location Count',
                                                value: attendances?.fakeLocationCount,
                                                key: 3 },
                                            { label: 'Broken Time Count',
                                                value: attendances?.brokenTimeCount,
                                                key: 1 },
                                            { label: 'Green Count', value: attendances?.greenCount, key: 0 },
                                            { label: 'Invalid Recognition Count',
                                                value: attendances?.invalidRecognitionCount,
                                                key: 5 },
                                            { label: 'Same Coordinates Count',
                                                value: attendances?.sameCoordinatesCount,
                                                key: 2 },
                                            { label: 'White Images Count',
                                                value: attendances?.whiteImagesCount,
                                                key: 4 },
                                        ].map((item) => (
                                            <div
                                                key={item.key}
                                                className="w-full flex items-center gap-[30px] justify-between"
                                            >
                                                <span className="text-[14px] font-[600] text-black/70">
                                                    {item.label}
                                                </span>
                                                <div className="flex items-center gap-[100px]">
                                                    <span className="text-gray-200 font-[600] text-[13px]">
                                                        {item.value || 0}
                                                    </span>
                                                    <button
                                                        className="px-[15px] py-[5px] rounded-[10px] text-[14px]
                                                 bg-main-100 text-white font-[500]"
                                                        onClick={() => handleDetailsClick({ category: item.key,
                                                            label: item.label,
                                                            detailsClick: 'attendancesBlank' })}
                                                    >
                                                        Details
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="w-full h-[200px] flex flex-col items-center justify-center">
                                        <EmptyFolderIcon />
                                    </div>
                                )
                            )}
                        </div>
                        <DuplicatedImages
                            entites={entities}
                            loading={getEntities?.loading}
                            startDate={startDate}
                            endDate={endDate}
                        />
                        {isNonEmptyObject(analytics?.devices) && (
                            <div className="w-[100%] flex flex-col items-start gap-[10px]
                             p-[15px] rounded-[6px] bg-white"
                            >
                                <span className="text-[14px] font-[600] text-black">Devices</span>
                                <div className="w-full flex items-center pb-[15px] border-b justify-between">
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Device ID</span>
                                    </div>
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Device name</span>
                                    </div>
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Device model</span>
                                    </div>
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">AppVersionCode</span>
                                    </div>
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Green Count</span>
                                    </div>
                                    <div className="w-[16.6%] flex  items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Details</span>
                                    </div>
                                    <div className="w-[16.6%] flex items-center justify-center">
                                        <span className="text-[14px] font-[500] text-black">Logs</span>
                                    </div>
                                </div>
                                {getAnalytics.loading ? (
                                    <div className="w-full h-[300px] flex flex-col ites-center justify-center">
                                        <LoadingSpinner />
                                    </div>
                                ) : (
                                    analytics ? (
                                        <div className="w-full flex flex-col h-[310px]
                                     overflow-auto items-center gap-[10px]"
                                        >
                                            {devices?.map((item) => (
                                                <div className="w-full flex items-center justify-between">
                                                    <div className="w-[16.6%] flex items-center justify-center">
                                                        <span className="text-[14px] text-center
                                                        font-[600] text-black/70"
                                                        >
                                                            {item?.deviceId}
                                                        </span>
                                                    </div>
                                                    <div className="w-[16.6%] flex items-center justify-center">
                                                        <span className="text-[14px] text-center
                                                        font-[600] text-black/70"
                                                        >
                                                            {item?.deviceName}
                                                        </span>
                                                    </div>
                                                    <div className="w-[16.6%]  flex items-center justify-center">
                                                        <span className="text-[13px] font-[600] text-gray-200">
                                                            {item?.deviceModel}
                                                        </span>
                                                    </div>
                                                    <div className="w-[16.6%] flex  items-center justify-center">
                                                        <span className="text-[13px] font-[600] text-gray-200">
                                                            {item?.maxAppVersionCode}
                                                        </span>
                                                    </div>
                                                    <div className="w-[16.6%] flex  items-center justify-center">
                                                        <span className="text-[13px] font-[600] text-gray-200">
                                                            {item?.greenCount}
                                                        </span>
                                                    </div>
                                                    <div className="flex w-[33.2%]  items-center gap-[30px]">
                                                        <div className="w-[50%] flex items-center justify-center">
                                                            <button
                                                                onClick={() => handleDetailsClick(
                                                                    {
                                                                        label: item.deviceId,
                                                                        detailsClick: 'devicesAnalytics',
                                                                        model: item.deviceModel,
                                                                    },
                                                                )}
                                                                className="px-[15px] font-[500]
                                                         py-[5px] bg-main-100 text-white
                                     rounded-[10px]"
                                                            >
                                                                Details
                                                            </button>
                                                        </div>
                                                        <div className="w-[50%] flex items-center justify-center">
                                                            <AnalyticsLogs
                                                                id={tenantEntity?.id}
                                                                deviceId={item?.deviceId}
                                                                title="Logs"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="w-full h-[200px] flex flex-col items-center justify-center">
                                            <EmptyFolderIcon />
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                        {isNonEmptyObject(packages) && (
                            <div className="w-full p-[10px] h-auto bg-white flex flex-col items-center">
                                <Table
                                    loading={getPackages.loading}
                                    page={page - 1}
                                    rowsPerPage={pageSize}
                                    onPageChange={(e, newPage) => setPage(newPage + 1)}
                                    onRowsPerPageChange={(e) => {
                                        setPageSize(parseInt(e.target.value, 10))
                                        setPage(1)
                                    }}
                                    cols={cols}
                                    rows={packages}
                                    totalCount={total}
                                    renderItem={(item) => (
                                        <PackagesTableItems
                                            key={item.id}
                                            item={item}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
